import { FormEvent, useEffect, useState } from "react";
import CustomButton from "../../../components/CustomButton";
import ContentCard from "../../../components/misc/ContentCard";
import TextInput from "../../../components/misc/TextInput";
import TableWidget from "../../../components/tables/TableWidget";
import { postScanRecallDocument } from "../../../redux/api/recall.api";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { clearRecallScan } from "../../../redux/reducer/recall.reducer";
import { recallRoutes } from "../../../router/routesConstants";
import Text from "../../../styleguide/Text";
import RecallReceiptForm from "./components/RecallReceiptForm";
import {
  getRecallScanDocumentTableData,
  getRecallScanTableData,
  recallScanDocumentTableHeader,
  recallScanTableHeader,
} from "./components/recallScanTableConfig";

function ReceiveRecallDocuments() {
  const dispatch = useAppDispatch();
  const { scan } = useAppSelector((state) => state.recall);
  const [barcodeToken, setBarcodeToken] = useState("");
  const [inputError, setInputError] = useState("");
  useEffect(() => {
    return () => {
      dispatch(clearRecallScan());
    };
  }, [dispatch]);

  const handleScan = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (scan.isLoading) return;
    const token = barcodeToken.trim();
    if (!/^[A-Z0-9-]{8,64}$/.test(token)) {
      setInputError("Introdu un cod valid de pe documentul de retragere");
      return;
    }
    setInputError("");
    dispatch(postScanRecallDocument({ barcodeToken: token }));
  };

  const document = scan.data;
  const isReceived = document?.status === "received";

  return (
    <>
      <ContentCard
        cardTitle="Primire documente de retragere"
        cardHeaderClassName="mb-4">
        <Text>
          Scaneaza codul de pe document, verifica datele si confirma primirea
          documentului la depozit.
        </Text>
        <form onSubmit={handleScan} noValidate className="mt-6">
          <TextInput
            name="barcodeToken"
            label="Cod document"
            autoFocus
            withClearButton
            value={barcodeToken}
            disabled={scan.isLoading}
            touched={Boolean(inputError)}
            error={inputError}
            onChange={(value) => {
              setBarcodeToken(String(value ?? ""));
              setInputError("");
              dispatch(clearRecallScan());
            }}
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CustomButton
              type="button"
              variant="outlined"
              navigate={`/${recallRoutes.recall}`}>
              Inapoi
            </CustomButton>
            <CustomButton
              type="submit"
              variant="contained"
              className="ml-auto"
              disabled={!barcodeToken.trim() || scan.isLoading}
              loading={scan.isLoading}>
              Verifica document
            </CustomButton>
          </div>
        </form>
        {scan.isError && (
          <Text role="alert" className="mt-4 text-danger">
            {scan.errorMessage}
          </Text>
        )}
      </ContentCard>
      {document && (
        <>
          <TableWidget
            className="[&_thead_th]:text-left"
            tableHeaderData={recallScanDocumentTableHeader}
            tableItemsData={getRecallScanDocumentTableData(document)}
            withTablePagination={false}
            tableLoading={scan.isLoading}
            CustomHeaderComponent={() => (
              <div className="mb-6 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-4 pl-1">
                  <Text
                    variant="h3"
                    component="h3"
                    className="min-w-0 flex-1 break-words font-semibold text-slate-900">
                    Retragere {document.documentNumber}
                  </Text>
                  {isReceived && (
                    <CustomButton
                      variant="contained"
                      navigate={`/${recallRoutes.recall}/${document.idCampaign}`}
                      tooltipTitle="Vezi retragerea completa">
                      Document primit
                    </CustomButton>
                  )}
                </div>
                <Text className="break-words">
                  Cod document: {document.barcodeToken}
                </Text>
              </div>
            )}
          />
          <TableWidget
            className="[&_thead_th]:text-left"
            tableTitle="Vanzari asociate documentului"
            tableHeaderData={recallScanTableHeader}
            tableItemsData={getRecallScanTableData(document.candidateSales)}
            withTablePagination={false}
          />
          {!isReceived && (
            <RecallReceiptForm
              key={document.barcodeToken}
              document={document}
              disabled={scan.isLoading}
            />
          )}
        </>
      )}
    </>
  );
}

export default ReceiveRecallDocuments;
